import React, { useMemo, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, fmtDate } from '../kit';
import { Pill, num, rowsOf, str } from '../ipintel/shared';
import { ExcelButton } from '../techpages/shared';
import { EXPORT_PATH, type HzCtx, fmtCost, fmtN, promptLabel, shortId } from './shared';

/**
 * Runs — GET horizon/runs: every Horizon run (sandbox samples from AI Labs and
 * the full batch runs of horizon/scripts/horizon_batch_run*.py), with its
 * prompt, model, filters and what it produced. A row opens Results filtered
 * to that run.
 */

type Res = { data: any; loading: boolean; error: string; reload: () => void };

const COLUMNS = ['created_at', 'id', 'prompt_name', 'model', 'sample_size', 'results', 'ok', 'techs', 'errors',
  'current_rows', 'total_tokens', 'total_cost', 'avg_latency_ms', 'first_at', 'last_at', 'filters'];
const LABELS: Record<string, string> = {
  created_at: 'Started', id: 'Run', prompt_name: 'Prompt', model: 'Model', sample_size: 'Sample size',
  results: 'Results', ok: 'Usable', techs: 'Technologies', errors: 'Errors', current_rows: 'Shown on pages',
  total_tokens: 'Tokens', total_cost: 'Cost',
  avg_latency_ms: 'Avg. latency', first_at: 'First result', last_at: 'Last result', filters: 'Filters',
};

/** filters is stored as JSON text; show "key: value" pairs. */
const filtersText = (v: any): string => {
  let o = v;
  if (typeof v === 'string') { try { o = JSON.parse(v); } catch { return v; } }
  if (!o || typeof o !== 'object') return str(o);
  return Object.entries(o).filter(([, x]) => x !== '' && x !== null && x !== undefined && x !== false)
    .map(([k, x]) => `${k}: ${typeof x === 'object' ? JSON.stringify(x) : x}`).join(' · ');
};

const HzRuns: React.FC<{ ctx: HzCtx; res: Res }> = ({ ctx, res }) => {
  const rows = useMemo(() => rowsOf(res.data, 'runs'), [res.data]);
  const [dlErr, setDlErr] = useState('');
  const tot = useMemo(() => rows.reduce((a: any, r: any) => ({
    results: a.results + num(r.results), techs: a.techs + num(r.techs), errors: a.errors + num(r.errors),
    tokens: a.tokens + num(r.total_tokens), cost: a.cost + num(r.total_cost),
  }), { results: 0, techs: 0, errors: 0, tokens: 0, cost: 0 }), [rows]);

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <KPIRow>
        <KPI label="Runs" value={fmtN(rows.length)} />
        <KPI label="Results" value={fmtN(tot.results)} hint="across all runs" />
        <KPI label="Errors" value={fmtN(tot.errors)} />
        <KPI label="Tokens" value={fmtN(tot.tokens)} />
        <KPI label="Cost" value={fmtCost(tot.cost)} />
      </KPIRow>
      <Section title="Runs"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <Btn small onClick={res.reload} disabled={res.loading}>Refresh</Btn>
            <ExcelButton path={EXPORT_PATH} params={{ kind: 'runs' }} onError={setDlErr} fallback="horizon-runs" what="All runs" />
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Every run, newest first: the AI Labs sandbox samples and the full batch runs. Click a run for its results, or a
          prompt for its full text.
        </p>
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading label="Reading runs…" /> : (
          <DataTable rows={rows} columns={COLUMNS} labels={LABELS} defaultSort="-created_at" csvName="horizon_runs"
            maxHeight="40rem" empty="No runs yet."
            render={{
              created_at: r => (
                <span className="text-xs" title={r.missing_run_row ? 'No run row — reconstructed from its results' : undefined}>
                  {fmtDate(r.created_at || r.first_at)}{r.missing_run_row ? ' *' : ''}
                </span>
              ),
              id: r => (
                <button type="button" onClick={() => ctx.goResults({ run: str(r.id) })} title={`${str(r.id)} — its results`}
                  className="font-mono text-xs underline underline-offset-2">{shortId(r.id)}</button>
              ),
              prompt_name: r => {
                const p = ctx.promptById(r.prompt_id);
                return (
                  <button type="button" onClick={() => ctx.goPrompt(str(r.prompt_id))} title={promptLabel(p, r.prompt_id)}
                    className="text-left inline-flex items-center gap-1.5 max-w-[20rem]">
                    <span className="truncate underline underline-offset-2">{str(r.prompt_name) || str(r.prompt_title) || p?.name || shortId(r.prompt_id)}</span>
                    {p?.is_final && <Pill tone="good">final</Pill>}
                  </button>
                );
              },
              results: r => <span className="tabular-nums font-medium">{fmtN(r.results)}</span>,
              ok: r => <span className="tabular-nums">{fmtN(r.ok)}</span>,
              current_rows: r => <span className={`tabular-nums ${num(r.current_rows) ? 'font-medium text-green-700' : ''}`}
                title="Technologies whose current (public) data came from this run">{fmtN(r.current_rows)}</span>,
              techs: r => <span className="tabular-nums">{fmtN(r.techs)}</span>,
              errors: r => <span className={`tabular-nums ${num(r.errors) ? 'text-red-600' : ''}`}>{fmtN(r.errors)}</span>,
              sample_size: r => <span className="tabular-nums">{fmtN(r.sample_size)}</span>,
              total_tokens: r => <span className="tabular-nums">{fmtN(r.total_tokens)}</span>,
              total_cost: r => <span className="tabular-nums">{fmtCost(r.total_cost)}</span>,
              avg_latency_ms: r => <span className="tabular-nums">{r.avg_latency_ms ? `${(num(r.avg_latency_ms) / 1000).toFixed(1)}s` : '—'}</span>,
              first_at: r => <span className="text-xs">{fmtDate(r.first_at)}</span>,
              last_at: r => <span className="text-xs">{fmtDate(r.last_at)}</span>,
              filters: r => {
                const t = filtersText(r.filters);
                return <span className="block max-w-[18rem] truncate text-xs text-grayscale-1200" title={t}>{t || '—'}</span>;
              },
            }}
            actions={r => (
              <span className="inline-flex gap-1.5">
                <Btn small onClick={() => ctx.goResults({ run: str(r.id) })}>Results →</Btn>
                {num(r.errors) > 0 && <Btn small onClick={() => ctx.goResults({ run: str(r.id), error: '1' })}>Errors</Btn>}
              </span>
            )} />
        )}
      </Section>
    </div>
  );
};

export default HzRuns;
